import React, { useState } from "react";

// mui components
import { Box, InputLabel, Stack, Tooltip } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useOrganizationId } from "../../../features/auth/useOrganizationId";
import { selectUserDetails } from "../../../features/auth/authSlice";
import {
	getCreditVendorDetailsQueryArgs,
	useGetCreditVendorDetailsQuery,
} from "../../../features/credit-reissue/creditVendorsApiSlice";

// custom components
import OutlinedTextInput from "../../custom-input/outlined-text-input";
import SecondarySelect from "../../custom-select/secondary-select";
import SecondarySelectLOLP from "../../custom-select/secondary-select-lo-lp";
import Heading5 from "../../typography/heading-05";
import Heading6 from "../../typography/heading-06";

// helper utils
import { creditReportOnOptions } from "../../../utils/select-options";
import { isEmptyOrUndefined } from "../../../utils/filter-value";
import {
	CLIENT_ID_ENABLED_PARTNERS,
	CREDIT_REISSUE_REPORT_ON_CONSTANTS,
	DEFAULT_CREDIT_CREDENTIALS_FORM,
} from "../../../utils/credit-reissue-constants";

// custom styles
import styles from "./index.module.scss";

const CreditCredentialsInformation = ({
	creditCredentialsDataIndex,
	creditCredentialsData,
	setCreditCredentialsData,
	creditVendorsData,
	loanApplicationData,
	disabled,
	error,
	// Opt-in asterisks on required labels (Current Loan Qualify). Off by default so
	// the Add New Loan wizard renders exactly as before.
	showRequiredMarks = false,
	// Opt-in (Current Loan Qualify, PathA-2.18): "Credit report product code" label with an
	// information bubble holding this text. Unset keeps the wizard's "Product Code" label.
	productCodeInfo,
}) => {
	const requiredLabel = (text, isRequired = true) =>
		showRequiredMarks && isRequired ? (
			<>
				{text}
				<span className={styles.requiredAsterisk}>*</span>
			</>
		) : (
			text
		);
	const [selectedVendor, setSelectedVendor] = useState({});
	const [selectedSavedVendorId, setSelectedSavedVendorId] = useState("");
	const organizationId = useOrganizationId();
	const userDetails = useSelector(selectUserDetails);
	const creditVendorDetailsQuery = getCreditVendorDetailsQueryArgs(
		organizationId,
		userDetails
	);
	const { currentData: savedVendors = [] } = useGetCreditVendorDetailsQuery(
		creditVendorDetailsQuery,
		{
			skip: !creditVendorDetailsQuery,
			refetchOnMountOrArgChange: true,
		}
	);
	const currentUserEmail = String(userDetails?.email ?? "").trim().toLowerCase();
	const currentUserSavedVendors = savedVendors.filter(
		(vendor) =>
			currentUserEmail &&
			String(vendor.user_email ?? "").trim().toLowerCase() ===
				currentUserEmail
	);
	const selectedSavedVendor = currentUserSavedVendors.find(
		(vendor) => String(vendor.id) === selectedSavedVendorId
	);
	const configuredVendorNames = new Set(
		currentUserSavedVendors.map((vendor) => vendor.creditVendor).filter(Boolean)
	);
	const uniqueCreditVendors = new Map();
	creditVendorsData.forEach((vendor) => {
		if (!uniqueCreditVendors.has(vendor.partner_name)) {
			uniqueCreditVendors.set(vendor.partner_name, vendor);
		}
	});
	const vendorOptions = Array.from(uniqueCreditVendors.values()).map(
		(vendor) => ({
			label: vendor.partner_name,
			value: vendor.partner_name,
		})
	);
	const selectedCreditVendor =
		selectedSavedVendor?.creditVendor ||
		creditCredentialsData[creditCredentialsDataIndex]?.creditVendor ||
		"";
	if (
		selectedCreditVendor &&
		!vendorOptions.some((vendor) => vendor.value === selectedCreditVendor)
	) {
		vendorOptions.push({
			label: selectedCreditVendor,
			value: selectedCreditVendor,
		});
	}
	const configuredVendorOptions = vendorOptions.filter((vendor) =>
		configuredVendorNames.has(vendor.value)
	);
	const unconfiguredVendorOptions = vendorOptions.filter(
		(vendor) => !configuredVendorNames.has(vendor.value)
	);
	const creditVendorOptions = configuredVendorOptions.length
		? [
				{
					label: "Configured",
					value: "__configured_header__",
					isSectionHeader: true,
				},
				...configuredVendorOptions,
				{
					label: "Unconfigured",
					value: "__unconfigured_header__",
					isSectionHeader: true,
				},
				...unconfiguredVendorOptions,
			]
		: vendorOptions;

	const selectSavedVendor = (event) => {
		const savedVendor = currentUserSavedVendors.find(
			(vendor) => String(vendor.id) === event.target.value
		);
		if (!savedVendor) return;

		const vendor = creditVendorsData.find(
			(item) => item.partner_name === savedVendor.creditVendor
		);
		setSelectedSavedVendorId(event.target.value);
		setSelectedVendor(vendor ?? {});
		setCreditCredentialsData(
			creditCredentialsData.map((data, index) =>
				index === creditCredentialsDataIndex
						? {
								...data,
								creditVendor: savedVendor.creditVendor,
								userName: savedVendor.userName ?? "",
								password:savedVendor.encrypted_password
										? "***"
										: "",
								encryptedPassword:savedVendor.encrypted_password ?? "",
							subscriberId: savedVendor.subscriberId ? "***" : "",
							prepopulatedSubscriberId: savedVendor.subscriberId ?? "",
							providerId: vendor?.id ?? "",
							productName: vendor?.product_name ?? "",
							reportOn:
								data.reportOn ||
								(loanApplicationData[creditCredentialsDataIndex * 2 + 1]
									?.firstName
									? CREDIT_REISSUE_REPORT_ON_CONSTANTS.JOINT
									: CREDIT_REISSUE_REPORT_ON_CONSTANTS.INDIVIDUAL),
						}
					: data
			)
		);
	};

	const setCreditVendor = (vendorName) => {
		const vendor = creditVendorsData.find(
			(item) => item.partner_name === vendorName
		);
		setSelectedVendor(vendor);
	};

	const selectCreditVendor = (event) => {
		const vendorName = event.target.value;

		if (!vendorName) {
			setSelectedSavedVendorId("");
			setSelectedVendor({});
			setCreditCredentialsData(
				creditCredentialsData.map((data, index) =>
					index === creditCredentialsDataIndex
						? {
								...data,
								creditVendor: "",
								clientID: "",
								subscriberId: "",
								prepopulatedSubscriberId: "",
								userName: "",
								password: "",
								encryptedPassword: "",
								productCode: "",
								productName: "",
								providerId: "",
							}
						: data
				)
			);
			return;
		}

		// Credentials may only be prepopulated from the authenticated user's
		// own provider configuration. If they have none, leave all credentials blank.
		const savedVendor = currentUserSavedVendors.find(
			(vendor) => vendor.creditVendor === vendorName
		);
		if (savedVendor) {
			selectSavedVendor({ target: { value: String(savedVendor.id) } });
			return;
		}

		setSelectedSavedVendorId("");
		setCreditCredentialsData(
			creditCredentialsData.map((data, index) => {
				if (creditCredentialsDataIndex === index) {
					return {
						...DEFAULT_CREDIT_CREDENTIALS_FORM,
						creditVendor: vendorName,
						providerId:
							creditVendorsData.find((item) => item.partner_name === vendorName)
								.id || "",
						productName:
							creditVendorsData.find((item) => item.partner_name === vendorName)
								?.product_name || "",
						reportOn: loanApplicationData[creditCredentialsDataIndex * 2 + 1]
							?.firstName
							? CREDIT_REISSUE_REPORT_ON_CONSTANTS.JOINT
							: CREDIT_REISSUE_REPORT_ON_CONSTANTS.INDIVIDUAL,
					};
				}
				return data;
			})
		);
		setCreditVendor(vendorName);
	};
	return (
		<Box className={styles.cardContainer}>
			<Stack direction="row" className={styles.cardHeaderContainer}>
				<Heading5 text={"Credit Credentials"} fontType="semibold" />
			</Stack>
			<Stack direction="column" className={styles.formSectionContainer}>
				{/* Credit Credentials Information */}
				<Stack direction="row" className={styles.inputSectionContainer}>
					{/* Credit Report Identifier */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor={`outlined-input-credit-report-id`}>
							<Heading6
								text={requiredLabel("Credit Report Identifier")}
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>

						<OutlinedTextInput
							type="text"
							id={`outlined-input-credit-report-id`}
							placeholder="Credit Report Identifier"
							value={
								creditCredentialsData[creditCredentialsDataIndex]
									?.creditReportIdentifier
							}
							handleChange={(event) =>
								setCreditCredentialsData(
									creditCredentialsData.map((data, index) => {
										if (creditCredentialsDataIndex === index) {
											return {
												...data,
												creditReportIdentifier: event.target.value,
											};
										} else {
											return data;
										}
									})
								)
							}
							disabled={disabled}
							error={
								error &&
								isEmptyOrUndefined(
									creditCredentialsData[creditCredentialsDataIndex]
										?.creditReportIdentifier
								)
							}
						/>
					</Stack>

					{/* Credit Vendor */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor={`outlined-input-credit-vendor`}>
							<Heading6
								text={requiredLabel("Credit Vendor")}
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>

						<SecondarySelectLOLP
							id={`outlined-input-credit-vendor`}
							value={selectedCreditVendor}
							options={creditVendorOptions}
							handleChange={selectCreditVendor}
							disabled={disabled}
							error={
								error &&
								isEmptyOrUndefined(
									creditCredentialsData[creditCredentialsDataIndex]
										?.creditVendor
								)
							}
						/>
					</Stack>

					{/* Client Id */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor={`outlined-input-client-id`}>
							<Heading6
								text={requiredLabel(
									"Client ID",
									CLIENT_ID_ENABLED_PARTNERS.includes(selectedVendor.partner_name)
								)}
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>

						<OutlinedTextInput
							type="text"
							id={`outlined-input-client-id`}
							placeholder="Client ID"
							value={
								creditCredentialsData[creditCredentialsDataIndex]?.clientID
							}
							handleChange={(event) =>
								setCreditCredentialsData(
									creditCredentialsData.map((data, index) => {
										if (creditCredentialsDataIndex === index) {
											return {
												...data,
												clientID: event.target.value,
											};
										} else {
											return data;
										}
									})
								)
							}
							disabled={
								disabled ||
								!CLIENT_ID_ENABLED_PARTNERS.includes(
									selectedVendor.partner_name
								)
							}
							error={
								CLIENT_ID_ENABLED_PARTNERS.includes(
									selectedVendor.partner_name
								) &&
								error &&
								isEmptyOrUndefined(
									creditCredentialsData[creditCredentialsDataIndex]?.clientID
								)
							}
						/>
					</Stack>

					{/* Subscriber Id */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor={`outlined-input-subscriber-id`}>
							<Heading6
								text="Subscriber ID"
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>

						<OutlinedTextInput
							type="text"
							id={`outlined-input-subscriber-id`}
							placeholder="Subscriber ID"
							value={
								creditCredentialsData[creditCredentialsDataIndex]?.subscriberId
							}
							handleChange={(event) =>
								setCreditCredentialsData(
									creditCredentialsData.map((data, index) => {
										if (creditCredentialsDataIndex === index) {
											return {
												...data,
												subscriberId: event.target.value,
												prepopulatedSubscriberId: "",
											};
										} else {
											return data;
										}
									})
								)
							}
							disabled={
								disabled ||
								!selectedVendor?.required_fields?.includes("subscriberId")
							}
						/>
					</Stack>

					{/* Login Id */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor={`outlined-input-login-id`}>
							<Heading6
								text={requiredLabel("Login ID")}
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>

						<OutlinedTextInput
							type="text"
							id={`outlined-input-login-id`}
							placeholder="Login ID"
							value={
								creditCredentialsData[creditCredentialsDataIndex]?.userName
							}
							handleChange={(event) =>
								setCreditCredentialsData(
									creditCredentialsData.map((data, index) => {
										if (creditCredentialsDataIndex === index) {
											return {
												...data,
												userName: event.target.value,
											};
										} else {
											return data;
										}
									})
								)
							}
							disabled={disabled}
							error={
								error &&
								isEmptyOrUndefined(
									creditCredentialsData[creditCredentialsDataIndex]?.userName
								)
							}
						/>
					</Stack>

					{/* Password */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor={`outlined-input-password`}>
							<Heading6
								text={requiredLabel("Password")}
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>

						<OutlinedTextInput
							type="text"
							id={`outlined-input-password`}
							placeholder="Password"
							value={
								creditCredentialsData[creditCredentialsDataIndex]?.password
							}
							handleChange={(event) =>
								setCreditCredentialsData(
									creditCredentialsData.map((data, index) => {
										if (creditCredentialsDataIndex === index) {
											return {
												...data,
												password: event.target.value,
												encryptedPassword: "",
											};
										} else {
											return data;
										}
									})
								)
							}
							disabled={disabled}
							error={
								error &&
								isEmptyOrUndefined(
									creditCredentialsData[creditCredentialsDataIndex]?.password
								)
							}
						/>
					</Stack>

					{/* Report On */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor={`outlined-input-report-on`}>
							<Heading6
								text={requiredLabel("Report On")}
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>

						<SecondarySelect
							id={`outlined-input-report-on`}
							placeholder="Report On"
							displayEmpty={true}
							value={
								creditCredentialsData[creditCredentialsDataIndex]?.reportOn ||
								""
							}
							handleChange={(event) =>
								setCreditCredentialsData(
									creditCredentialsData.map((data, index) => {
										if (creditCredentialsDataIndex === index) {
											return {
												...data,
												reportOn: event.target.value,
											};
										} else {
											return data;
										}
									})
								)
							}
							options={creditReportOnOptions}
							disabled={disabled}
							error={
								error &&
								isEmptyOrUndefined(
									creditCredentialsData[creditCredentialsDataIndex]?.reportOn
								)
							}
						/>
					</Stack>

					{/* Product Code */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor={`outlined-input-product-code`}>
							{productCodeInfo ? (
								<Stack direction="row" alignItems="center" gap={0.5}>
									<Heading6
										text="Credit report product code"
										fontType="semibold"
										extraClass={styles.inputHeader}
									/>
									<Tooltip title={productCodeInfo} arrow>
										<InfoOutlined
											fontSize="small"
											aria-label={productCodeInfo}
											data-testid="product-code-info"
											className={styles.infoIcon}
										/>
									</Tooltip>
								</Stack>
							) : (
								<Heading6
									text="Product Code"
									fontType="semibold"
									extraClass={styles.inputHeader}
								/>
							)}
						</InputLabel>

						<OutlinedTextInput
							type="text"
							id={`outlined-input-product-code`}
							placeholder="Product Code"
							value={
								creditCredentialsData[creditCredentialsDataIndex]?.productCode
							}
							handleChange={(event) =>
								setCreditCredentialsData(
									creditCredentialsData.map((data, index) => {
										if (creditCredentialsDataIndex === index) {
											return {
												...data,
												productCode: event.target.value,
											};
										} else {
											return data;
										}
									})
								)
							}
							disabled={
								disabled ||
								!selectedVendor?.required_fields?.includes("productCode")
							}
						/>
					</Stack>

					{/* Provider Id */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor={`outlined-input-provider-id`}>
							<Heading6
								text={requiredLabel("Provider ID")}
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>

						<OutlinedTextInput
							type="text"
							id={`outlined-input-provider-id`}
							placeholder="Provider ID"
							value={
								creditCredentialsData[creditCredentialsDataIndex]?.providerId
							}
							disabled={true}
						/>
					</Stack>
				</Stack>
			</Stack>
		</Box>
	);
};

export default CreditCredentialsInformation;
